import type {
  TripContext,
  WatchAlertView,
} from "@b2bportal/air-price-watch-api";
import { useModuleBEM, useAirPriceWatchStyles } from "@b2bportal/core-themes";
import { CallState, CoreAirPriceWatchComponent } from "@b2bportal/core-types";
import { Loading } from "@b2bportal/core-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { WatchCard } from "@tenant/air-price-watch";
import clsx from "clsx";
import {
  AirPriceWatchRawEventName,
  useEmitCoreAirPriceWatchBeforeLoad,
} from "../../tracking";
import defaultStyles from "./CancelWatchModalContent.module.scss";
import { useCancelWatchModalContent } from "./hooks";

export interface CancelWatchModalContentComponentProps {
  watch: WatchAlertView;
  tripContext: TripContext;
  onClose?: () => void;
  refetch: () => void;
}

const COMPONENT_KEY = CoreAirPriceWatchComponent.CancelWatchModalContent;

export const CancelWatchModalContent = (
  props: CancelWatchModalContentComponentProps
) => {
  const styles = useAirPriceWatchStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);
  const { t } = useI18nContext();
  const {
    context: { watch, tripContext, callState },
    handlers: { onCancelWatch, onClose, refetch, onTryAgain },
  } = useCancelWatchModalContent(props);

  useEmitCoreAirPriceWatchBeforeLoad(
    {
      name: AirPriceWatchRawEventName.AirPriceWatchRemoved,
      properties: {
        watch,
        tripContext,
      },
    },
    callState === CallState.Success
  );

  return (
    <div className={clsx(block)}>
      <WatchCard
        watch={watch}
        tripContext={tripContext}
        showActions={false}
        refetch={refetch}
      />
      {callState === CallState.NotCalled ? (
        <>
          <div className={cn("stop-watch-description")}>
            {t("watch.unwatchMessage")}
          </div>
          <button
            onClick={onCancelWatch}
            className={cn("primary-watch-action")}
          >
            {t("watch.stopWatchButtonLabel")}
          </button>
          <button className={cn("secondary-watch-action")} onClick={onClose}>
            {t("cancel")}
          </button>
        </>
      ) : callState === CallState.InProcess ? (
        <div className="loading">
          <Loading />
        </div>
      ) : callState === CallState.Success ? (
        <>
          <div className={cn("stop-watch-description")}>
            {t("watch.stopWatchSuccessModal")}
          </div>
          <button
            onClick={() => {
              onClose?.();
              refetch();
            }}
            className={cn("primary-watch-action")}
          >
            {t("done")}
          </button>
        </>
      ) : (
        <>
          <div className={cn("stop-watch-description")}>{t("error.title")}</div>
          <button onClick={onTryAgain} className={cn("primary-watch-action")}>
            {t("tryAgain")}
          </button>
        </>
      )}
    </div>
  );
};
